/**
 * useAISummary Hook
 *
 * Hook for fetching AI summary for wallet contacts in inbox-v3.
 * Only fetches when contact source is 'wallet'.
 */

import {useMemo} from 'react'
import {useGetRawContactByMobileQuery} from '../../../../../services/cellcast.v2'

export interface UseAISummaryOptions {
  /** Phone number to fetch AI summary for */
  phoneNumber?: string
  /** Contact source (only fetches if 'wallet') */
  contactSource?: string
  /** Whether to enable the query */
  enabled?: boolean
}

export interface UseAISummaryReturn {
  /** AI Summary note content */
  aiSummaryNote: string | null
  /** Status of the AI summary */
  statusAiSummaryNote: string | null
  /** Whether the summary is loading */
  isLoading: boolean
  /** Whether the summary is being fetched */
  isFetching: boolean
  /** Error message if any */
  error: string | null
  /** Whether this contact has an AI summary */
  hasAISummary: boolean
  /** Whether this is a wallet contact (should show AI summary card) */
  isWalletContact: boolean
  /** Refetch the AI summary */
  refetch: () => void
}

export function useAISummary(options: UseAISummaryOptions = {}): UseAISummaryReturn {
  const {phoneNumber, contactSource, enabled = true} = options

  // Clean phone number - remove country code prefix if present
  const cleanPhoneNumber = useMemo(() => {
    if (!phoneNumber) return ''
    // Remove leading + or country codes like 61
    return phoneNumber.replace(/^\+?61/, '').replace(/\D/g, '')
  }, [phoneNumber])

  // Only fetch if contact source is 'wallet' or 'Wallet'
  const isWalletContact = useMemo(() => {
    if (!contactSource) return false
    return contactSource.toLowerCase() === 'wallet'
  }, [contactSource])

  // Fetch raw contact data
  const {
    data: response,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useGetRawContactByMobileQuery(
    {phoneNumber: cleanPhoneNumber},
    {
      skip: !enabled || !cleanPhoneNumber || !isWalletContact,
      refetchOnMountOrArgChange: true,
    }
  )

  // Extract AI summary from response
  const aiSummaryNote = useMemo(() => {
    if (!response?.data?.aiSummaryNote) return null
    return response.data.aiSummaryNote
  }, [response])

  const statusAiSummaryNote = useMemo(() => {
    if (!response?.data?.statusAiSummaryNote) return null
    return response.data.statusAiSummaryNote
  }, [response])

  // Check if contact has AI summary
  const hasAISummary = useMemo(() => {
    return !!aiSummaryNote && aiSummaryNote.trim().length > 0
  }, [aiSummaryNote])

  // Error handling
  const error = useMemo(() => {
    if (queryError) {
      return 'Failed to load AI summary'
    }
    return null
  }, [queryError])

  return {
    aiSummaryNote,
    statusAiSummaryNote,
    isLoading,
    isFetching,
    error,
    hasAISummary,
    isWalletContact,
    refetch,
  }
}

export default useAISummary